import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCategoryTabId, getGRETab, getTabs } from '@client/redux/modules/mylounge';
import { findActiveTab } from '@client/components/mylounge/banner/helpers';
import { scrollTo } from '@client/helpers';
import { CAMPAIGN_WRAPPER, THB_OUTER_WRAPPER } from '@client/components/mylounge/constants';
import { getElementRealHeight } from '@client/helpers/dom';
import { trackSelectContent } from '@browsing/tracking';
import { CategoryTabIds } from '@shared/constants';
import { getCookie } from '@pegasus/utils';
import { createTabListWithForYou } from '../../forYou/helpers';
import Tabs from './Tabs';

export const TabsWrapper = () => {
    const navigate = useNavigate();
    const responseTabs = useSelector(getTabs);

    const isForYouFeature = getCookie('forYouFeature') === 'true'; // TODO: check octopus imlementation for forYou feature flag
    const tabs = isForYouFeature ? createTabListWithForYou(responseTabs) : responseTabs;
    const activeTabIdFromUrl = useLocation();
    const stadardExperienceCategoryTabId = useSelector(getCategoryTabId);
    const categoryTabId = isForYouFeature
        ? activeTabIdFromUrl.pathname.split('/').pop()
        : stadardExperienceCategoryTabId;
    const [activeIndex, setActiveIndex] = useState(findActiveTab(tabs, categoryTabId));
    const [lastIndex, setLastIndex] = useState(null);
    const greTab = useSelector(getGRETab);

    const changeActiveTab = useCallback(
        (newActiveIndex: number) =>
            navigate(`/event/${tabs?.[newActiveIndex]?.id}${window.location.search}`),
        [navigate, tabs],
    );

    const isSameTab = useCallback(
        (newActiveIndex: number) => newActiveIndex === activeIndex,
        [activeIndex],
    );

    const isGRETab = useCallback(
        (index: number) => {
            if (!greTab || !tabs || !tabs[index]) return false;
            return tabs[index].id === greTab.id;
        },
        [tabs, greTab],
    );

    const trackTabEvent = useCallback(
        (index: number) => {
            const { id } = tabs[index];

            trackSelectContent('category_tab', 'click', { contentId: id });
        },
        [tabs],
    );

    const allTab = tabs?.find(tab => tab.id === CategoryTabIds.ALL);
    const isNoThbVariant = allTab ? !allTab.thb : false;

    const handleTabChange = useCallback(
        (newActiveIndex: number) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(newActiveIndex);
            changeActiveTab(newActiveIndex);

            // This is a temporary solution to scroll to the top of the page when the user clicks on the GRE tab
            // Currently, without this solution browser window scrolls down and miss the first campaign on GRE tab
            if (isGRETab(newActiveIndex)) {
                scrollTo({ top: 80 });
                return;
            }

            const wrapper = document.getElementById(CAMPAIGN_WRAPPER);
            const thbWrapper = document.getElementById(THB_OUTER_WRAPPER);

            if (wrapper) {
                const isNavigatingToAllTab = tabs?.[newActiveIndex]?.id === CategoryTabIds.ALL;
                if (isNoThbVariant && isNavigatingToAllTab) {
                    scrollTo({ top: 0 });
                    return;
                }
                const height = getElementRealHeight(thbWrapper);
                const { offsetTop } = wrapper;
                const top = offsetTop - height;

                scrollTo({ top });
            }
        },
        [isSameTab, trackTabEvent, changeActiveTab, isGRETab, isNoThbVariant, tabs],
    );

    const handleTabClick = useCallback(
        (newActiveIndex: number) => handleTabChange(newActiveIndex),
        [handleTabChange],
    );

    useEffect(() => {
        const newActiveIndex = findActiveTab(tabs, categoryTabId);
        if (newActiveIndex !== activeIndex) {
            setActiveIndex(newActiveIndex);
            setLastIndex(activeIndex);
        }
    }, [activeIndex, categoryTabId, tabs]);

    return (
        <Tabs
            tabs={tabs}
            // @ts-ignore
            onTabClick={handleTabClick}
            activeIndex={activeIndex}
            lastIndex={lastIndex}
        />
    );
};
